/**
 * The pre-battle board: a dense field of floating coloured pixels.
 *
 * Two rules shape this file, and both come from how the mini-game is meant to
 * work rather than from convenience.
 *
 * **The displayed colour is the truth.** Each pixel is given a colour to show,
 * and that colour is then encoded to exactly one anchor through `encodeColour`,
 * the same function the sprite profiles were built with. Region scores are how
 * the colour is *chosen*; they are never counted as recipe mass. A pixel that
 * looks purple is therefore one purple pixel, not a share of red and a share of
 * blue, no matter what mixture of regions produced it.
 *
 * **The board is reproducible.** Everything is drawn from one seeded generator,
 * so a board seed reproduces the same board exactly. That is what lets a test
 * assert a board is the same twice, and what lets a bug report name a seed.
 */

import { COLLECT_CONFIG, type BoardLayoutFamily } from './collectConfig';
import { COLOUR_ANCHORS, anchorIndexById, encodeColourIndex, type Rgb } from './colour';
import { profileFor, profilesOn, type ColourProfile } from './colourProfiles';
import { seededRandom } from './game';
import { creaturesOn } from './roster';
import type { ContinentId } from './types';

export interface PlayableRect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * The space the floating interface hangs over, in logical units.
 *
 * These are passed in rather than read from the config by the board, because the
 * interface is drawn in CSS pixels and the board is drawn in logical units: the
 * same interface is a different number of logical units on a narrow screen than
 * on a wide one. The screen measures its own boxes and converts, so the field ends
 * exactly where the interface begins at every width. `COLLECT_CONFIG`'s own values
 * are the reference for a 360-CSS-pixel-wide screen, which is what the sweep and
 * the tests use.
 */
export interface BoardReserves {
  readonly reservedTop: number;
  readonly reservedBottom: number;
}

/**
 * One collectible pixel.
 *
 * The mutable parts of a pixel's life - being pulled off its home, being
 * gathered - belong to the simulation, not here. This is the pixel's identity:
 * where it belongs, how it idles, and what colour it is.
 */
export interface BoardPixel {
  readonly homeX: number;
  readonly homeY: number;
  /** Idle floating amplitude in logical units. */
  readonly amplitude: number;
  readonly frequencyX: number;
  readonly frequencyY: number;
  readonly phaseX: number;
  readonly phaseY: number;
  /** The colour as displayed. Authoritative, and the only thing that is encoded. */
  readonly rgb: Rgb;
  /** The single anchor the displayed colour encodes to. */
  readonly anchorIndex: number;
  /**
   * True when this is an any-colour pixel rather than a colour.
   *
   * An any-colour pixel is one of every colour at once, so it belongs to no
   * anchor: it is counted separately by the simulation and folded into the
   * encounter as mass on every anchor. `anchorIndex` is still filled in - it is
   * the colour the pixel was carved from - but nothing may read it as the
   * pixel's colour. It is not part of `anchorShares`, either.
   */
  readonly wildcard: boolean;
}

/**
 * A uniform grid over the pixels, for finding the ones near a point without
 * testing every pixel.
 *
 * Cell size accounts for the largest idle float amplitude, so a pixel is always
 * in its home cell or an immediate neighbour. Queries expand by the amplitude as
 * well, which is what keeps a pixel that has drifted into the next cell from
 * being missed.
 */
export interface SpatialGrid {
  readonly cellSize: number;
  readonly cols: number;
  readonly rows: number;
  readonly originX: number;
  readonly originY: number;
  readonly cells: ReadonlyArray<readonly number[]>;
  /** Largest idle float amplitude on the board, in logical units. */
  readonly maxFloatAmplitude: number;
}

export interface CollectBoard {
  readonly seed: number;
  readonly continent: ContinentId;
  readonly width: number;
  readonly height: number;
  /**
   * The field: where the pixels are laid out, and the bounds the seed moves
   * within - dragged, and bounced off while it flies.
   *
   * One rectangle serves both, so the seed can never leave the field the player
   * can see, and no part of the board is drawn where the seed is not allowed to
   * go. It clears the floating interface at both ends: the seed-fill meter and
   * the target panel at the top, and the readout at the bottom.
   */
  readonly playable: PlayableRect;
  /** Where the seed appears: the middle of the field. */
  readonly spawn: { readonly x: number; readonly y: number };
  readonly spacing: number;
  readonly family: BoardLayoutFamily;
  readonly pixels: readonly BoardPixel[];
  /**
   * How many of the pixels are any-colour rather than a colour.
   *
   * Stated separately because they are excluded from `anchorShares` and from the
   * colour the simulation counts, so a readout or a report that wants the whole
   * field needs this as well as the shares.
   */
  readonly wildcardPixels: number;
  readonly grid: SpatialGrid;
  /**
   * How much of the board each anchor actually occupies, measured from the
   * encoded pixels rather than from the region recipe. This is what the
   * pre-launch readout shows, so it cannot promise a colour the board does not
   * really have.
   */
  readonly anchorShares: ReadonlyArray<{ readonly anchorIndex: number; readonly share: number }>;
  /** The colour pool the board was asked for, kept for diagnostics. */
  readonly requestedPalette: readonly number[];
  /** Minimum pixels each anchor was promised by the plan. */
  readonly promisedPixels: ReadonlyMap<number, number>;
  /**
   * Everything that went wrong but did not stop the board being built: a
   * recoverable allocation problem, or the reason a fallback was used. Empty on a
   * clean build, and never silently dropped.
   */
  readonly problems: readonly string[];
  /** True when generation failed its checks and the validated fallback board was used. */
  readonly usedFallback: boolean;
  /** The plan the board was built from, including the target profiles it serves. */
  readonly plan: BoardPlan;
  /**
   * The generator's working stages, present only when `withStages` was requested.
   * A measurement aid for the report and tests; play never asks for it.
   */
  readonly stages?: BoardStages;
}

/**
 * The colours a region carries, and in what proportions.
 *
 * A region states a recipe rather than a single colour, so one large readable
 * shape can hold several visible colours in controlled amounts. That is what
 * lets a board carry a creature's real colour ratios without splitting the
 * board into a dozen small patches.
 */
interface RegionRecipeEntry {
  readonly anchorIndex: number;
  readonly share: number;
}

interface Region {
  readonly kind: 'gauss' | 'ring';
  /** The colours this region contributes, as proportion of the region's influence. */
  readonly recipe: readonly RegionRecipeEntry[];
  readonly x: number;
  readonly y: number;
  readonly radiusX: number;
  readonly radiusY: number;
  readonly angle: number;
  readonly strength: number;
}

/**
 * What a board is asked to carry, before any pixels exist.
 *
 * This holds the **complete target profiles**, not a summary of them: the ratio
 * relationships inside each profile are what a creature is, and summarising them
 * into one number per colour would throw away the thing being matched. The per
 * anchor floors are derived from the profiles so that no demanded colour can be
 * silently absent, and any adjustment to them is recorded rather than hidden.
 */
export interface BoardPlan {
  /** The anchors the board may use. Nothing outside this may end up on the board. */
  readonly palette: readonly number[];
  /** Minimum share of the board each anchor must occupy, after any budget adjustment. */
  readonly floors: ReadonlyMap<number, number>;
  /** The target profiles the floors were derived from. */
  readonly targetProfiles: readonly ColourProfile[];
  /** Recorded reasons the plan could not be taken as asked. Empty when none applied. */
  readonly problems: readonly string[];
}

/**
 * Derives a board plan from a set of target profiles.
 *
 * Every anchor that any profile asks for gets a floor, so availability is a
 * property of the plan rather than a hope about the generator. The floors are
 * not proportions of each creature's mixture - they only promise that a colour
 * exists in usable quantity. Whether a creature's *ratios* can be gathered is a
 * separate question, answered by the sweep's simulated launches and never by the
 * board's composition.
 *
 * If the floors together exceed `availabilityBudget`, they are scaled down
 * proportionally and the reduction is recorded. An over-subscribed budget is
 * recoverable, so it degrades and reports rather than failing.
 */
export function planForProfiles(
  profiles: readonly ColourProfile[],
  neutralHexes: readonly string[],
): BoardPlan {
  const floors = new Map<number, number>();
  const demanded = new Set<number>();
  for (const profile of profiles) {
    for (const weight of profile.weights) demanded.add(anchorIndexById(weight.anchor));
  }
  for (const hex of neutralHexes) demanded.add(encodeColourIndex(hexToRgb(hex)));
  // The neutral colours also have to be showable, so they are part of the palette
  // even when no profile asks for them.
  const palette = [...demanded].sort((a, b) => a - b);
  if (palette.length < 2) {
    throw new Error(
      `Garaimon: a board plan was asked for ${palette.length} colour(s), which cannot make a mixture. ` +
        'This is an impossible input rather than an unlucky seed.',
    );
  }

  const problems: string[] = [];
  const rawFloor = COLLECT_CONFIG.anchorFloorShare;
  for (const anchorIndex of palette) floors.set(anchorIndex, rawFloor);
  const total = rawFloor * palette.length;
  if (total > COLLECT_CONFIG.availabilityBudget) {
    const scale = COLLECT_CONFIG.availabilityBudget / total;
    for (const anchorIndex of palette) floors.set(anchorIndex, rawFloor * scale);
    problems.push(
      `availability floors for ${palette.length} colours wanted ${(total * 100).toFixed(0)}% of the board, ` +
        `over the ${(COLLECT_CONFIG.availabilityBudget * 100).toFixed(0)}% budget, so every floor was scaled to ` +
        `${(rawFloor * scale * 100).toFixed(2)}%. This is a recoverable allocation problem, not a failure.`,
    );
  }
  return { palette, floors, targetProfiles: profiles, problems };
}

/** One region's shape and recipe, exposed for measurement rather than for play. */
export interface RegionDiagnostic {
  readonly kind: 'gauss' | 'ring';
  readonly x: number;
  readonly y: number;
  readonly radiusX: number;
  readonly radiusY: number;
  readonly angle: number;
  readonly strength: number;
  readonly recipe: readonly { readonly anchorIndex: number; readonly share: number }[];
}

/**
 * The generator's three working stages, captured for measurement.
 *
 * The generator does not realize its intended proportions directly. It computes
 * an intended local distribution per pixel (stage A), gives each pixel whichever
 * colour is strongest there (stage B), then recolours the cheapest pixels to
 * bring any colour below its promised count up to it (stage C). These three
 * captures exist so the gap between "what the recipes asked for" and "what the
 * board actually carries" can be measured instead of assumed, and so the repair
 * step's real effect - how many pixels it moves, and whether it keeps shapes -
 * is a number rather than a claim.
 *
 * Captured only when `withStages` is requested, so play pays nothing for it.
 */
export interface BoardStages {
  /** The palette, in the same column order the flattened arrays use. */
  readonly palette: readonly number[];
  readonly columns: number;
  /** Stage A: per-pixel intended probabilities, flattened as `pixel * columns + column`. */
  readonly intended: Float64Array;
  /** Stage A totalled over the board, converted to expected pixel counts. */
  readonly intendedCounts: readonly number[];
  /** Stage B: the column each pixel takes by strongest value, before any repair. */
  readonly strongestColumns: Int32Array;
  /** Stage C: the column each pixel ends up with. */
  readonly finalColumns: Int32Array;
  /** Lattice column and row of each pixel, for neighbour comparisons. */
  readonly latticeCols: Int32Array;
  readonly latticeRows: Int32Array;
  /** Index of every pixel the repair moved. */
  readonly repairedPixelIndices: readonly number[];
  /**
   * For each repaired pixel, the strength it gave up: its strongest colour's
   * weight minus the weight of the colour it was moved to. Near zero means the
   * move was almost free, which is what a boundary pixel looks like.
   */
  readonly repairedCosts: readonly number[];
  /** The intended-but-not-guaranteed `w1 - w2` margin at each pixel, before repair. */
  readonly margins: Float64Array;
  readonly regions: readonly RegionDiagnostic[];
  /** The pixel count each column was promised. */
  readonly promises: readonly number[];
  /**
   * One flag per lattice pixel: 1 when the pixel was carved out to be an
   * any-colour pixel. These pixels are not part of any column's final count.
   */
  readonly wildcardFlags: Uint8Array;
}

/** Idle float offset for a pixel at a given time. */
export function floatOffsetX(pixel: BoardPixel, time: number): number {
  return pixel.amplitude * Math.sin(pixel.frequencyX * time + pixel.phaseX);
}

export function floatOffsetY(pixel: BoardPixel, time: number): number {
  return pixel.amplitude * Math.cos(pixel.frequencyY * time + pixel.phaseY);
}

/** Where a pixel actually is at a given time, including its idle float. */
export function pixelPosition(pixel: BoardPixel, time: number): { x: number; y: number } {
  return { x: pixel.homeX + floatOffsetX(pixel, time), y: pixel.homeY + floatOffsetY(pixel, time) };
}

/**
 * The field for a board of a given size: the pixels' layout and the seed's bounds.
 *
 * It is computed once, explicitly, so the space reserved for the floating panels
 * above and the readout below is a decision rather than an accident of layout, and
 * so tests use the same rectangle the game does. Everything inside it is both
 * drawn as pixels and reachable by the seed.
 *
 * `reserves` defaults to the config, which is the 360-CSS-pixel-wide reference. A
 * screen measures its own overlay boxes and passes the converted values, so the
 * field reaches the interface instead of stopping short of it on a wide screen.
 */
export function playableRect(width: number, height: number, reserves: BoardReserves = COLLECT_CONFIG): PlayableRect {
  const half = COLLECT_CONFIG.pixelSpacing / 2;
  return {
    left: half,
    top: reserves.reservedTop,
    right: width - half,
    bottom: height - reserves.reservedBottom,
  };
}

/** A board height in logical units for a container, matching how the battle sizes its canvas. */
export function logicalHeightFor(width: number, height: number): number {
  if (width <= 0 || height <= 0) return COLLECT_CONFIG.fallbackLogicalHeight;
  return Math.round((COLLECT_CONFIG.logicalWidth * height) / width);
}

/**
 * The colour pool a board offers: the colours every continent creature's own
 * profile is made of, plus shared neutrals.
 *
 * It is built from the creatures' profiles rather than from the two display
 * colours in the roster, because those are not the same list. A creature with
 * measured sprite art has a profile of a dozen or more colours, and if the board
 * offered only its two roster colours then most of what that creature is made of
 * would not exist on the board at all - a target that cannot be gathered, which
 * the readout would then be advertising.
 *
 * Even so, having a colour available is not a promise that any particular ratio
 * of it is reachable. That is what the launch sweep measures, and it is why the
 * pre-launch readout shows colours without claiming they can be matched.
 */
export function boardPalette(continent: ContinentId): number[] {
  const indices = new Set<number>();
  for (const creature of creaturesOn(continent)) {
    indices.add(encodeColourIndex(rosterColourToRgb(creature.color)));
    indices.add(encodeColourIndex(rosterColourToRgb(creature.accent)));
    for (const weight of profileFor(creature.id).weights) indices.add(anchorIndexById(weight.anchor));
  }
  for (const hex of COLLECT_CONFIG.neutralColours) indices.add(encodeColourIndex(hexToRgb(hex)));
  return [...indices].sort((a, b) => a - b);
}

/**
 * The share of a creature's profile that this board actually carries.
 *
 * A board shows a handful of the palette's colours while a profile is made of
 * anywhere from two to fourteen, so on some boards a creature has almost none of
 * its colours present. That is the pre-launch question a player can act on, and
 * unlike "how close a launch can get" it needs no simulation: it is measured
 * directly from what the pixels on this board encode to.
 *
 * It is stated as colours present, never as a chance of appearing. A colour being
 * on the board does not mean a launch can gather it in the proportions the
 * creature is made of - that is what the launch sweep measures.
 */
export function profilePresence(profile: ColourProfile, anchorShares: ReadonlyArray<{ anchorIndex: number; share: number }>): number {
  const carried = new Set(anchorShares.map((entry) => entry.anchorIndex));
  let present = 0;
  for (const weight of profile.weights) {
    if (carried.has(anchorIndexById(weight.anchor))) present += weight.share;
  }
  return Math.max(0, Math.min(1, present));
}

function hexToRgb(value: string): Rgb {
  const text = value.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(text)) throw new Error(`Garaimon: "${value}" is not a six-digit hex colour.`);
  return [Number.parseInt(text.slice(0, 2), 16), Number.parseInt(text.slice(2, 4), 16), Number.parseInt(text.slice(4, 6), 16)];
}

/** Roster colours are stored as 0xRRGGBB numbers, so they need the same treatment. */
function rosterColourToRgb(value: number): Rgb {
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

interface RegionGeometry {
  readonly kind: 'gauss' | 'ring';
  readonly x: number;
  readonly y: number;
  readonly radiusX: number;
  readonly radiusY: number;
  readonly angle: number;
  readonly strength: number;
}

/**
 * Builds region shapes for a layout family.
 *
 * Shape only: the colours come from `assignRecipes`, so geometry and colour can
 * be reasoned about separately. The number of regions scales with how many
 * creatures the board has to serve, because each creature needs its own stretch
 * of board to be reachable through - a board with six regions for five creatures
 * leaves at least one creature with nowhere to be gathered. The count is bounded
 * by `maxRegionsPerBoard`, and the sweep reports any creature left out.
 */
function buildRegionGeometry(
  family: BoardLayoutFamily,
  profileCount: number,
  playable: PlayableRect,
  random: () => number,
): RegionGeometry[] {
  const width = playable.right - playable.left;
  const height = playable.bottom - playable.top;
  const centreX = playable.left + width / 2;
  const centreY = playable.top + height / 2;
  const span = Math.min(width, height);
  const angle = random() * Math.PI;
  const targetRegions = Math.max(3, Math.round(profileCount * COLLECT_CONFIG.regionsPerProfile));
  /** A layout family's own jitter must not push the board past the readable count. */
  const clampRegions = (count: number): number => Math.max(3, Math.min(COLLECT_CONFIG.maxRegionsPerBoard, count));

  if (family === 'bands') {
    // Stripes across the board at a random angle. One axis is stretched so each
    // region reads as a band rather than a blob.
    const count = clampRegions(targetRegions + Math.round(random() * 2 - 1));
    const regions: RegionGeometry[] = [];
    const across = (width + height) / 2;
    // The bands have to share the board, so the more of them there are the
    // thinner each one is. A fixed thickness would overlap its neighbours into a
    // mush at thirteen creatures and read as speckle at five.
    const spacing = (across * 0.85) / count;
    const thickness = Math.max(span * 0.1, spacing * (0.45 + random() * 0.25));
    for (let index = 0; index < count; index += 1) {
      const offset = ((index + 0.5) / count - 0.5) * across * 0.85;
      const normal = angle + Math.PI / 2;
      regions.push({
        kind: 'gauss',
        x: centreX + Math.cos(normal) * offset,
        y: centreY + Math.sin(normal) * offset,
        radiusX: span * 0.95,
        radiusY: thickness,
        angle,
        strength: 0.9 + random() * 0.5,
      });
    }
    return regions;
  }

  if (family === 'islands') {
    const count = clampRegions(targetRegions + Math.round(random() * 2 - 1));
    const regions: RegionGeometry[] = [];
    for (let index = 0; index < count; index += 1) {
      // A grid of cells, one island per cell, so islands cover the board rather
      // than clumping and leaving whole stretches with one colour.
      const cellCols = Math.ceil(Math.sqrt(count));
      const cellRows = Math.ceil(count / cellCols);
      const cellWidth = width / cellCols;
      const cellHeight = height / cellRows;
      const cellX = (index % cellCols) * cellWidth;
      const cellY = Math.floor(index / cellCols) * cellHeight;
      const radius = span * (0.17 + random() * 0.1);
      regions.push({
        kind: 'gauss',
        x: playable.left + cellX + cellWidth * (0.2 + random() * 0.6),
        y: playable.top + cellY + cellHeight * (0.2 + random() * 0.6),
        radiusX: radius,
        radiusY: radius * (0.75 + random() * 0.5),
        angle: random() * Math.PI,
        strength: 0.85 + random() * 0.5,
      });
    }
    return regions;
  }

  // Centre against perimeter: a blob in the middle and rings that strengthen
  // toward the edges, so the two readings are genuinely opposite. Extra blobs
  // give the remaining creatures somewhere to live.
  const regions: RegionGeometry[] = [
    {
      kind: 'gauss',
      x: centreX + (random() - 0.5) * width * 0.1,
      y: centreY + (random() - 0.5) * height * 0.1,
      radiusX: span * (0.2 + random() * 0.12),
      radiusY: span * (0.2 + random() * 0.12),
      angle,
      strength: 1 + random() * 0.4,
    },
    {
      kind: 'ring',
      x: centreX,
      y: centreY,
      radiusX: span * (0.34 + random() * 0.14),
      radiusY: span * (0.34 + random() * 0.14),
      angle: 0,
      strength: 0.85 + random() * 0.4,
    },
  ];
  const extra = Math.max(0, clampRegions(targetRegions) - 2);
  for (let index = 0; index < extra; index += 1) {
    const ring = index % 2 === 1;
    regions.push({
      kind: ring ? 'ring' : 'gauss',
      x: centreX + (random() - 0.5) * width * 0.7,
      y: centreY + (random() - 0.5) * height * 0.7,
      radiusX: span * (0.16 + random() * 0.14),
      radiusY: span * (0.16 + random() * 0.14),
      angle: random() * Math.PI,
      strength: 0.8 + random() * 0.5,
    });
  }
  return regions;
}

/**
 * Gives each region a recipe drawn from the targets themselves.
 *
 * A board has to serve several creatures at once, and their profiles disagree -
 * so no single board-wide mixture can match any of them. What can be made to
 * match is a *place*: a stretch of board whose local mixture resembles one
 * creature's colours in that creature's proportions. A launch through it then
 * gathers something close to that target.
 *
 * So each region's recipe is derived from one target profile, reduced to the
 * colours that profile leans on most and renormalised. Regions cycle through the
 * profiles, so every creature on the continent gets at least one stretch of board
 * shaped like it. The leading colour keeps a clear majority of the region, which
 * is what keeps the result readable as shapes rather than as texture soup.
 *
 * If a creature's profile leans on very few colours, its region is correspondingly
 * simple; if it leans on many, the region carries many. That is the profile's own
 * complexity showing through, not a cap applied by the generator.
 */
function assignRecipes(
  geometry: readonly RegionGeometry[],
  plan: BoardPlan,
  random: () => number,
): Region[] {
  const profiles = plan.targetProfiles;
  const paletteSet = new Set(plan.palette);
  // A rotation, so which region sits where changes between boards even though the
  // recipe set does not.
  const rotation = profiles.length > 0 ? Math.floor(random() * profiles.length) : 0;
  return geometry.map((shape, index) => {
    const profile = profiles.length > 0 ? profiles[(index + rotation) % profiles.length] : undefined;
    const ranked = profile
      ? [...profile.weights]
          .map((weight) => ({ anchorIndex: anchorIndexById(weight.anchor), share: weight.share }))
          .filter((entry) => paletteSet.has(entry.anchorIndex))
          .sort((a, b) => b.share - a.share)
          .slice(0, Math.max(2, COLLECT_CONFIG.regionRecipeColours))
      : [];
    if (ranked.length === 0) {
      const fallbackAnchor = plan.palette[Math.floor(random() * plan.palette.length)] ?? plan.palette[0]!;
      ranked.push({ anchorIndex: fallbackAnchor, share: 1 });
    }

    // Keep the profile's own proportions, lifting the leading colour only if the
    // profile itself is flatter than the readability floor.
    const total = ranked.reduce((sum, entry) => sum + entry.share, 0);
    const lead = ranked[0]!;
    const leadShare = Math.max(COLLECT_CONFIG.recipeLeadFloor, total > 0 ? lead.share / total : 1);
    const othersTotal = ranked.slice(1).reduce((sum, entry) => sum + entry.share, 0);
    const recipe: RegionRecipeEntry[] = [{ anchorIndex: lead.anchorIndex, share: leadShare }];
    if (othersTotal > 0) {
      const scale = (1 - leadShare) / othersTotal;
      for (const entry of ranked.slice(1)) recipe.push({ anchorIndex: entry.anchorIndex, share: entry.share * scale });
    }
    const sum = recipe.reduce((acc, entry) => acc + entry.share, 0);
    return { ...shape, recipe: recipe.map((entry) => ({ anchorIndex: entry.anchorIndex, share: entry.share / sum })) };
  });
}


function regionScore(region: Region, x: number, y: number, softness: number): number {
  const cos = Math.cos(-region.angle);
  const sin = Math.sin(-region.angle);
  const dx = x - region.x;
  const dy = y - region.y;
  const localX = dx * cos - dy * sin;
  const localY = dx * sin + dy * cos;
  const radiusX = region.radiusX * (1 + softness * 0.25);
  const radiusY = region.radiusY * (1 + softness * 0.25);
  const distance = (localX * localX) / (2 * radiusX * radiusX) + (localY * localY) / (2 * radiusY * radiusY);
  if (region.kind === 'ring') {
    // Strong far from the centre, weak at it, so a perimeter colour reads as a
    // ring rather than as another blob.
    return region.strength * (1 - Math.exp(-distance));
  }
  return region.strength * Math.exp(-distance);
}

/** A smooth, low-frequency wave field, one per anchor, giving regions irregular edges. */
function noiseField(random: () => number, anchorCount: number) {
  const waves: Array<Array<{ weight: number; angle: number; frequency: number; phase: number }>> = [];
  for (let anchor = 0; anchor < anchorCount; anchor += 1) {
    const perAnchor: Array<{ weight: number; angle: number; frequency: number; phase: number }> = [];
    for (let wave = 0; wave < COLLECT_CONFIG.noiseWaves; wave += 1) {
      perAnchor.push({
        weight: 1 / (wave + 1),
        angle: random() * Math.PI * 2,
        frequency: (COLLECT_CONFIG.noiseFrequencyBase + wave * COLLECT_CONFIG.noiseFrequencyStep),
        phase: random() * Math.PI * 2,
      });
    }
    waves.push(perAnchor);
  }
  const totalWeight = waves[0]?.reduce((sum, wave) => sum + wave.weight, 0) ?? 1;
  return (anchor: number, x: number, y: number): number => {
    let sum = 0;
    for (const wave of waves[anchor] ?? []) sum += wave.weight * Math.sin(wave.frequency * (Math.cos(wave.angle) * x + Math.sin(wave.angle) * y) + wave.phase);
    return sum / totalWeight;
  };
}

/**
 * One pixel's position, idle motion, and where it sits on the hidden lattice.
 *
 * The lattice coordinates are kept because the quota rounding walks the board in
 * blocks, and a block is a rectangle of lattice cells rather than of screen
 * space, so a pixel's home is placed by counting rather than by measuring.
 */
interface LatticePixel {
  readonly homeX: number;
  readonly homeY: number;
  readonly latticeCol: number;
  readonly latticeRow: number;
  readonly amplitude: number;
  readonly frequencyX: number;
  readonly frequencyY: number;
  readonly phaseX: number;
  readonly phaseY: number;
}

/** How much of a region's influence belongs to a particular colour. */
function recipeShare(recipe: readonly RegionRecipeEntry[], anchorIndex: number): number {
  let share = 0;
  for (const entry of recipe) if (entry.anchorIndex === anchorIndex) share += entry.share;
  return share;
}

/** Lays out the pixel lattice: one home per cell, nudged, with idle motion drawn from the seed. */
function generateLattice(
  random: () => number,
  playable: PlayableRect,
  spacing: number,
  jitter: number,
  amplitude: number,
): LatticePixel[] {
  const pixels: LatticePixel[] = [];
  const [frequencyLow, frequencyHigh] = COLLECT_CONFIG.floatFrequencyRange;
  const inset = spacing / 2 + jitter + amplitude;
  let latticeRow = 0;
  for (let y = playable.top + inset; y <= playable.bottom - inset; y += spacing) {
    let latticeCol = 0;
    for (let x = playable.left + inset; x <= playable.right - inset; x += spacing) {
      pixels.push({
        homeX: x + (random() - 0.5) * 2 * jitter,
        homeY: y + (random() - 0.5) * 2 * jitter,
        latticeCol,
        latticeRow,
        amplitude,
        frequencyX: frequencyLow + random() * (frequencyHigh - frequencyLow),
        frequencyY: frequencyLow + random() * (frequencyHigh - frequencyLow),
        phaseX: random() * Math.PI * 2,
        phaseY: random() * Math.PI * 2,
      });
      latticeCol += 1;
    }
    latticeRow += 1;
  }
  return pixels;
}

/**
 * The colour distribution each pixel would ideally show.
 *
 * This is an expectation, not a decision. It is the normalized sum of every
 * region's recipe weighted by how strongly that region reaches the pixel, so a
 * pixel inside one region leans to that region's lead colour, a pixel between
 * two regions leans to the blend of their recipes, and the numbers add to one.
 *
 * Nothing here picks a colour. Picking is `distributeAnchors`, which realizes
 * these expectations as whole pixels while keeping the promised floors.
 */
function pixelDistributions(
  pixels: readonly LatticePixel[],
  regions: readonly Region[],
  palette: readonly number[],
  plan: BoardPlan,
  noise: (anchor: number, x: number, y: number) => number,
  noiseAmplitude: number,
): Float64Array {
  const columns = palette.length;
  const out = new Float64Array(pixels.length * columns);
  // Every colour a target asked for starts with a small advantage, so the
  // expectation itself already leans toward keeping demanded colours present.
  const baselines = palette.map((anchorIndex) => 0.05 + (plan.floors.get(anchorIndex) ?? 0) * 2);
  // A slice of every pixel's expectation is spent on the plan's floors, spread
  // evenly over the board. Without this the region recipes alone decide which
  // colours can exist, and a colour only a minority region asks for could end up
  // with less expectation than the plan promised it.
  const floorMix = COLLECT_CONFIG.floorMixShare;
  const floorTotal = palette.reduce((sum, anchorIndex) => sum + (plan.floors.get(anchorIndex) ?? 0), 0);
  const floorPart = palette.map((anchorIndex) => (floorTotal > 0 ? (plan.floors.get(anchorIndex) ?? 0) / floorTotal : 1 / columns));
  for (let index = 0; index < pixels.length; index += 1) {
    const pixel = pixels[index]!;
    let total = 0;
    for (let column = 0; column < columns; column += 1) {
      const anchorIndex = palette[column]!;
      let weight = baselines[column]!;
      for (const region of regions) {
        const share = recipeShare(region.recipe, anchorIndex);
        if (share > 0) weight += share * regionScore(region, pixel.homeX, pixel.homeY, COLLECT_CONFIG.regionSoftness);
      }
      weight += noiseAmplitude * noise(column, pixel.homeX, pixel.homeY);
      if (weight < 0) weight = 0;
      out[index * columns + column] = weight;
      total += weight;
    }
    if (total <= 0) {
      out[index * columns] = 1;
      total = 1;
    }
    for (let column = 0; column < columns; column += 1) {
      const regionPart = out[index * columns + column]! / total;
      out[index * columns + column] = (1 - floorMix) * regionPart + floorMix * floorPart[column]!;
    }
  }
  return out;
}

/**
 * Chooses one colour per pixel, keeping the board readable and the promises met.
 *
 * Two steps, in this order, and the order matters.
 *
 * **1. Each pixel takes its strongest colour.** Readability is spatial: a board
 * reads as shapes rather than confetti when neighbouring pixels usually agree.
 * Any scheme that forces each small block to reproduce the local distribution
 * exactly cannot do that, because it deliberately splits every block among all
 * the colours the distribution mentions - so the finer the quota, the more
 * speckled the result. Taking the strongest colour lets the smooth part of the
 * expectation (regions and their recipes) decide the large shapes, which is what
 * the eye reads.
 *
 * **2. Every promised colour is topped up to its floor.** Step 1 alone can leave
 * a colour that is only a minority everywhere with too few pixels, or none at
 * all - the exact failure this rework exists to fix. So each colour below its
 * promised count takes pixels from whichever colour is furthest above its own
 * promise, always choosing the pixel that loses least by changing. That is a
 * quota rule, and it is a stronger guarantee than rounding could give: the
 * promised availability is exact rather than hoped for, and because the cheapest
 * pixels to change sit at region edges, the repairs keep the shapes intact.
 *
 * Ties in step 1 are broken by a seeded draw, so a board is still reproducible
 * from its seed alone.
 */
function distributeAnchors(
  pixels: readonly LatticePixel[],
  weights: Float64Array,
  palette: readonly number[],
  promises: ReadonlyMap<number, number>,
  random: () => number,
): {
  readonly columnOfPixel: Int32Array;
  readonly strongestColumns: Int32Array;
  readonly repairedPixelIndices: readonly number[];
  readonly repairedCosts: readonly number[];
  readonly margins: Float64Array;
  readonly problems: readonly string[];
} {
  const columns = palette.length;
  const problems: string[] = [];
  const columnOfPixel = new Int32Array(pixels.length).fill(-1);
  const counts = new Int32Array(columns);
  const margins = new Float64Array(pixels.length);

  const jitter = new Float64Array(columns);
  for (let index = 0; index < pixels.length; index += 1) {
    for (let column = 0; column < columns; column += 1) jitter[column] = weights[index * columns + column]! + random() * 1e-9;
    let bestColumn = 0;
    let bestWeight = Number.NEGATIVE_INFINITY;
    let secondWeight = Number.NEGATIVE_INFINITY;
    for (let column = 0; column < columns; column += 1) {
      const value = jitter[column]!;
      if (value > bestWeight) {
        secondWeight = bestWeight;
        bestWeight = value;
        bestColumn = column;
      } else if (value > secondWeight) {
        secondWeight = value;
      }
    }
    columnOfPixel[index] = bestColumn;
    // The margin is how close the runner-up was, which is the local definition of
    // a boundary: near zero means this pixel sat between two colours.
    margins[index] = secondWeight === Number.NEGATIVE_INFINITY ? bestWeight : bestWeight - secondWeight;
    counts[bestColumn]! += 1;
  }
  // Stage B, kept before the repair touches anything.
  const strongestColumns = Int32Array.from(columnOfPixel);
  const repairedPixelIndices: number[] = [];
  const repairedCosts: number[] = [];

  const order = palette
    .map((anchorIndex, column) => ({ column, anchorIndex, promise: promises.get(anchorIndex) ?? 0 }))
    .filter((entry) => counts[entry.column]! < entry.promise)
    .sort((a, b) => b.promise - counts[b.column]! - (a.promise - counts[a.column]!));
  for (const entry of order) {
    while (counts[entry.column]! < entry.promise) {
      // The donor is whichever colour is furthest above its *own* promise, so a
      // top-up can never push another promised colour below its floor. Because
      // the promises together are a fraction of the board, a donor always exists.
      let donor = -1;
      let bestSpare = 0;
      for (let column = 0; column < columns; column += 1) {
        if (column === entry.column) continue;
        const spare = counts[column]! - (promises.get(palette[column]!) ?? 0);
        if (spare > bestSpare) {
          bestSpare = spare;
          donor = column;
        }
      }
      if (donor < 0) break;
      // Moving a pixel costs the difference in how strongly the two colours want
      // it, so the cheapest moves are the boundary pixels - though "cheapest" is a
      // preference, not a guarantee that every move lands on a boundary.
      let bestPixel = -1;
      let bestCost = Number.POSITIVE_INFINITY;
      for (let index = 0; index < pixels.length; index += 1) {
        if (columnOfPixel[index]! !== donor) continue;
        const cost = weights[index * columns + donor]! - weights[index * columns + entry.column]!;
        if (cost < bestCost) {
          bestCost = cost;
          bestPixel = index;
        }
      }
      if (bestPixel < 0) break;
      columnOfPixel[bestPixel] = entry.column;
      counts[donor]! -= 1;
      counts[entry.column]! += 1;
      repairedPixelIndices.push(bestPixel);
      repairedCosts.push(bestCost);
    }
    if (counts[entry.column]! < entry.promise) {
      problems.push(
        `colour ${COLOUR_ANCHORS[entry.anchorIndex]?.id ?? entry.anchorIndex} was promised ${entry.promise} pixels ` +
          `but only ${counts[entry.column]!} could be placed.`,
      );
    }
  }
  return { columnOfPixel, strongestColumns, repairedPixelIndices, repairedCosts, margins, problems };
}

/** Turns placed columns into pixels drawn in their anchor's exact colour. */
function assemblePixels(
  lattice: readonly LatticePixel[],
  columnOfPixel: Int32Array,
  palette: readonly number[],
  wildcardFlags: Uint8Array,
): BoardPixel[] {
  return lattice.map((pixel, index) => {
    const anchorIndex = palette[columnOfPixel[index]!] ?? palette[0]!;
    return {
      homeX: pixel.homeX,
      homeY: pixel.homeY,
      amplitude: pixel.amplitude,
      frequencyX: pixel.frequencyX,
      frequencyY: pixel.frequencyY,
      phaseX: pixel.phaseX,
      phaseY: pixel.phaseY,
      // The visible colour is the anchor's own colour, and the very same index is
      // what the simulation counts. There is no blended colour anywhere, so what
      // the player sees is exactly what they gather.
      rgb: hexToRgb(COLOUR_ANCHORS[anchorIndex]!.hex),
      anchorIndex,
      wildcard: wildcardFlags[index] === 1,
    };
  });
}

/**
 * Carves the any-colour pixels out of colours that can spare them.
 *
 * An any-colour pixel is a pixel of every colour at once, and it is what keeps
 * every Garaimon possible even when the player's throw has gathered almost
 * nothing. Taken from the board's ordinary pixels, so the field stays the size the
 * layout made it.
 *
 * A colour may only give one up while it is *above* its promised floor, so
 * carving them can never take a promised colour below the quantity the plan
 * promised it. That is the same donor rule the anchor repair uses, and it is why
 * this cannot quietly break availability. The pixels are chosen from a shuffled
 * order, so they end up spread across the board rather than clumped in one region.
 *
 * If fewer can be carved out than were wanted, that is reported rather than
 * accepted in silence; the field still gets whatever it could spare.
 */
function chooseWildcards(
  columnOfPixel: Int32Array,
  palette: readonly number[],
  promises: ReadonlyMap<number, number>,
  count: number,
  random: () => number,
): { readonly flags: Uint8Array; readonly problems: readonly string[] } {
  const flags = new Uint8Array(columnOfPixel.length);
  if (count <= 0) return { flags, problems: [] };
  const counts = new Int32Array(palette.length);
  for (let index = 0; index < columnOfPixel.length; index += 1) counts[columnOfPixel[index]!]! += 1;

  const order = Array.from({ length: columnOfPixel.length }, (_value, index) => index);
  for (let index = order.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(random() * (index + 1));
    const held = order[index]!;
    order[index] = order[swap]!;
    order[swap] = held;
  }

  const donated = new Map<number, number>();
  let placed = 0;
  for (const index of order) {
    if (placed >= count) break;
    const column = columnOfPixel[index]!;
    const promise = promises.get(palette[column]!) ?? 0;
    const already = donated.get(column) ?? 0;
    if (counts[column]! - already - 1 < promise) continue;
    flags[index] = 1;
    donated.set(column, already + 1);
    placed += 1;
  }

  const problems: string[] = [];
  if (placed < count) {
    problems.push(
      `${count} any-colour pixel(s) were wanted but only ${placed} could be carved out of colours that were above their promised floors.`,
    );
  }
  return { flags, problems };
}

/** Options for building a board. */
export interface BoardBuildOptions {
  /**
   * Throw instead of falling back when generation fails on supported content.
   * Tests and developer mode pass true so a defect is loud; play leaves it false
   * so an unlucky seed can never block a player.
   */
  readonly throwOnFailure?: boolean;
  /** Capture the three working stages on the returned board, for measurement. */
  readonly withStages?: boolean;
  /**
   * Space to keep clear for the floating interface, in logical units. Defaults to
   * the config's 360-CSS-pixel reference; a screen measures its own overlays and
   * passes the converted values so the field reaches the interface exactly.
   */
  readonly reserves?: BoardReserves;
}

/** The pixel count each anchor is promised, from the plan's floors. */
function promisesFor(plan: BoardPlan, pixelCount: number): Map<number, number> {
  const promises = new Map<number, number>();
  for (const anchorIndex of plan.palette) {
    promises.set(anchorIndex, Math.max(1, Math.round((plan.floors.get(anchorIndex) ?? 0) * pixelCount)));
  }
  return promises;
}

function assembleBoard(
  seed: number,
  continent: ContinentId,
  width: number,
  height: number,
  plan: BoardPlan,
  family: BoardLayoutFamily,
  pixels: readonly BoardPixel[],
  spacing: number,
  amplitude: number,
  playable: PlayableRect,
  problems: readonly string[],
  usedFallback: boolean,
  stages?: BoardStages,
): CollectBoard {
  if (pixels.length === 0) {
    throw new Error(
      `Garaimon: building the board for ${continent} at ${width}x${height} produced no pixels. ` +
        'The playable area is smaller than the reserved space plus one pixel, which is an impossible input rather than an unlucky seed.',
    );
  }
  const counts = new Map<number, number>();
  let wildcardPixels = 0;
  for (const pixel of pixels) {
    if (pixel.wildcard) {
      wildcardPixels += 1;
      continue;
    }
    counts.set(pixel.anchorIndex, (counts.get(pixel.anchorIndex) ?? 0) + 1);
  }
  // Measured over the colour pixels only: an any-colour pixel is not a colour, so
  // counting it here would inflate every hero's presence on the board.
  const colourPixels = pixels.length - wildcardPixels;
  const anchorShares = colourPixels <= 0
    ? []
    : [...counts.entries()]
        .map(([anchorIndex, count]) => ({ anchorIndex, share: count / colourPixels }))
        .sort((a, b) => b.share - a.share);
  return {
    seed,
    continent,
    width,
    height,
    playable,
    spawn: { x: (playable.left + playable.right) / 2, y: (playable.top + playable.bottom) / 2 },
    spacing,
    family,
    pixels,
    wildcardPixels,
    grid: buildGrid(pixels, playable, spacing, amplitude),
    anchorShares,
    requestedPalette: plan.palette,
    promisedPixels: promisesFor(plan, pixels.length),
    problems,
    usedFallback,
    plan,
    ...(stages === undefined ? {} : { stages }),
  };
}

/** The generation attempt, before any failure handling wraps it. */
function attemptBoard(
  seed: number,
  continent: ContinentId,
  width: number,
  height: number,
  plan: BoardPlan,
  family: BoardLayoutFamily,
  reserves: BoardReserves,
  withStages = false,
): { readonly board: CollectBoard; readonly shortfalls: readonly string[] } {
  const random = seededRandom(seed ^ 0x9e3779b9);
  const playable = playableRect(width, height, reserves);
  const spacing = COLLECT_CONFIG.pixelSpacing;
  const jitter = COLLECT_CONFIG.pixelJitterRatio * spacing;
  const amplitude = COLLECT_CONFIG.floatAmplitudeRatio * spacing;
  const geometry = buildRegionGeometry(family, plan.targetProfiles.length, playable, random);
  const regions = assignRecipes(geometry, plan, random);
  const noise = noiseField(random, plan.palette.length);
  const meanStrength = regions.reduce((sum, region) => sum + region.strength, 0) / Math.max(1, regions.length);
  const noiseAmplitude = COLLECT_CONFIG.noiseAmplitudeRatio * meanStrength;
  const lattice = generateLattice(random, playable, spacing, jitter, amplitude);
  if (lattice.length === 0) {
    throw new Error(
      `Garaimon: the board for ${continent} at ${width}x${height} produced no pixels. ` +
        'The playable area is smaller than the reserved space plus one pixel, which is an impossible input rather than an unlucky seed.',
    );
  }
  const weights = pixelDistributions(lattice, regions, plan.palette, plan, noise, noiseAmplitude);
  const promises = promisesFor(plan, lattice.length);
  const distributed = distributeAnchors(lattice, weights, plan.palette, promises, random);
  // Carved after the promises are met, and only from colours above their own
  // floor, so the field's any-colour pixels can never break availability.
  const wildcardWanted = Math.max(1, Math.round(lattice.length * COLLECT_CONFIG.wildcardShare));
  const carved = chooseWildcards(distributed.columnOfPixel, plan.palette, promises, wildcardWanted, random);
  const pixels = assemblePixels(lattice, distributed.columnOfPixel, plan.palette, carved.flags);

  let stages: BoardStages | undefined;
  if (withStages) {
    const columns = plan.palette.length;
    const intendedCounts = new Array<number>(columns).fill(0);
    for (let index = 0; index < lattice.length; index += 1) {
      for (let column = 0; column < columns; column += 1) intendedCounts[column]! += weights[index * columns + column]!;
    }
    stages = {
      palette: plan.palette,
      columns,
      intended: weights,
      intendedCounts,
      strongestColumns: distributed.strongestColumns,
      finalColumns: distributed.columnOfPixel,
      latticeCols: Int32Array.from(lattice.map((pixel) => pixel.latticeCol)),
      latticeRows: Int32Array.from(lattice.map((pixel) => pixel.latticeRow)),
      repairedPixelIndices: distributed.repairedPixelIndices,
      repairedCosts: distributed.repairedCosts,
      margins: distributed.margins,
      regions: regions.map((region) => ({
        kind: region.kind,
        x: region.x,
        y: region.y,
        radiusX: region.radiusX,
        radiusY: region.radiusY,
        angle: region.angle,
        strength: region.strength,
        recipe: region.recipe.map((entry) => ({ anchorIndex: entry.anchorIndex, share: entry.share })),
      })),
      promises: plan.palette.map((anchorIndex) => promises.get(anchorIndex) ?? 0),
      wildcardFlags: carved.flags,
    };
  }

  const board = assembleBoard(
    seed,
    continent,
    width,
    height,
    plan,
    family,
    pixels,
    spacing,
    amplitude,
    playable,
    [...plan.problems, ...distributed.problems, ...carved.problems],
    false,
    stages,
  );
  return { board, shortfalls: distributed.problems };
}

/**
 * A banded board used when generation fails on content the generator supports.
 *
 * Bands are the most even layout available, so a seed the generator cannot
 * satisfy still produces something playable. It is *validated*, not trusted: the
 * promises are checked exactly as for a normal board, and if the fallback cannot
 * meet them either then the input is impossible rather than unlucky, which is the
 * one case that raises instead of falling back.
 *
 * Exported so a test can run it against the current profiles and motion settings.
 * A fallback that was valid for one set of profiles or physics can go stale, and
 * the only way to know is to keep checking it.
 */
export function buildFallbackBoard(
  seed: number,
  plan: BoardPlan,
  continent: ContinentId,
  width: number,
  height: number,
  options: BoardBuildOptions = {},
): CollectBoard {
  const reserves = options.reserves ?? COLLECT_CONFIG;
  const fallback = attemptBoard(seed, continent, width, height, plan, 'bands', reserves, options.withStages === true);
  if (fallback.shortfalls.length > 0) {
    throw new Error(
      `Garaimon: the fallback board for ${continent} ${width}x${height} seed ${seed} could not place every promised colour ` +
        `(${fallback.shortfalls.join('; ')}), so this is an impossible input rather than an unlucky seed.`,
    );
  }
  if (options.throwOnFailure === true) return fallback.board;
  return {
    ...fallback.board,
    usedFallback: true,
    problems: [...fallback.board.problems, 'A validated banded fallback board was used.'],
  };
}

/**
 * A board built for a specific plan rather than for a continent.
 *
 * Separated from `buildBoard` so a test can drive the generator with a target
 * that is not in the roster - which is the only way to check that the generator
 * will still work when the art team ships a creature with more colours than
 * anything currently in the game.
 */
export function buildBoardForPlan(
  seed: number,
  plan: BoardPlan,
  continent: ContinentId,
  width: number,
  height: number,
  options: BoardBuildOptions = {},
): CollectBoard {
  const random = seededRandom(seed ^ 0x9e3779b9);
  const family = COLLECT_CONFIG.layoutFamilies[Math.floor(random() * COLLECT_CONFIG.layoutFamilies.length)] ?? 'islands';
  const reserves = options.reserves ?? COLLECT_CONFIG;
  const first = attemptBoard(seed, continent, width, height, plan, family, reserves, options.withStages === true);
  if (first.shortfalls.length === 0) return first.board;

  const reason =
    `the board for ${continent} ${width}x${height} seed ${seed} could not place every promised colour: ` +
    `${first.shortfalls.join('; ')}`;
  if (options.throwOnFailure === true) throw new Error(`Garaimon: ${reason}.`);
  // A supported-content generation failure. The fallback is validated, and if it
  // cannot satisfy the promises either then the input was impossible, so
  // `buildFallbackBoard` raises and the failure is reported as such.
  const fallback = buildFallbackBoard(seed, plan, continent, width, height, { throwOnFailure: true, withStages: options.withStages === true, reserves });
  return {
    ...fallback,
    usedFallback: true,
    problems: [
      ...fallback.problems.filter((problem) => problem !== 'A validated banded fallback board was used.'),
      `${reason}. A validated banded fallback board was used instead.`,
    ],
  };
}

/**
 * Builds a board for a continent.
 *
 * The pixels sit on a hidden grid, one per cell, so the field is dense and even
 * rather than clumped and gappy, and each is then nudged by a small random
 * offset so the grid does not read as a grid. Which colours they show is decided
 * by the plan's floors and the regions' recipes, then realized by quota rounding,
 * never by a single winner per pixel.
 */
export function buildBoard(
  seed: number,
  continent: ContinentId,
  width: number,
  height: number,
  options: BoardBuildOptions = {},
): CollectBoard {
  const plan = planForProfiles(profilesOn(continent), COLLECT_CONFIG.neutralColours);
  return buildBoardForPlan(seed, plan, continent, width, height, options);
}


/** Builds the uniform grid described on `SpatialGrid`. */
export function buildGrid(
  pixels: ReadonlyArray<{ homeX: number; homeY: number }>,
  playable: PlayableRect,
  spacing: number,
  maxFloatAmplitude: number,
): SpatialGrid {
  const cellSize = spacing + 2 * maxFloatAmplitude;
  if (!(cellSize > 0)) throw new Error('Garaimon: the spatial grid needs a positive cell size.');
  const originX = playable.left - maxFloatAmplitude;
  const originY = playable.top - maxFloatAmplitude;
  const cols = Math.max(1, Math.ceil((playable.right - playable.left + 2 * maxFloatAmplitude) / cellSize));
  const rows = Math.max(1, Math.ceil((playable.bottom - playable.top + 2 * maxFloatAmplitude) / cellSize));
  const cells: number[][] = Array.from({ length: cols * rows }, () => []);
  pixels.forEach((pixel, index) => {
    const col = Math.max(0, Math.min(cols - 1, Math.floor((pixel.homeX - originX) / cellSize)));
    const row = Math.max(0, Math.min(rows - 1, Math.floor((pixel.homeY - originY) / cellSize)));
    cells[row * cols + col]!.push(index);
  });
  return { cellSize, cols, rows, originX, originY, cells, maxFloatAmplitude };
}

/**
 * Collects the indices of pixels whose home could be within `radius` of a point,
 * into `out` to avoid allocating on every call.
 *
 * The search area is widened by the largest float amplitude, which is what makes
 * it safe for pixels that have drifted away from their homes.
 */
export function queryGridInto(grid: SpatialGrid, x: number, y: number, radius: number, out: number[]): number[] {
  out.length = 0;
  const reach = radius + grid.maxFloatAmplitude;
  const minCol = Math.max(0, Math.floor((x - reach - grid.originX) / grid.cellSize));
  const maxCol = Math.min(grid.cols - 1, Math.floor((x + reach - grid.originX) / grid.cellSize));
  const minRow = Math.max(0, Math.floor((y - reach - grid.originY) / grid.cellSize));
  const maxRow = Math.min(grid.rows - 1, Math.floor((y + reach - grid.originY) / grid.cellSize));
  for (let row = minRow; row <= maxRow; row += 1) {
    for (let col = minCol; col <= maxCol; col += 1) {
      const cell = grid.cells[row * grid.cols + col];
      if (!cell) continue;
      for (const index of cell) out.push(index);
    }
  }
  return out;
}

/**
 * Picks a board seed that the player has not just seen. Re-rolling uses the same
 * seed list the game already keeps, so repeat-avoidance needs no new state.
 */
export function rollBoardSeed(recentSeeds: readonly number[], createSeedValue: () => number): number {
  for (let attempt = 0; attempt < 16; attempt += 1) {
    const candidate = createSeedValue();
    if (!recentSeeds.includes(candidate)) return candidate;
  }
  return createSeedValue();
}

/** Every creature colour the board's continent offers, for a pre-launch target readout. */
export function continentColourHexes(continent: ContinentId): string[] {
  const seen = new Set<string>();
  for (const creature of creaturesOn(continent)) {
    seen.add(`#${creature.color.toString(16).padStart(6, '0')}`);
    seen.add(`#${creature.accent.toString(16).padStart(6, '0')}`);
  }
  return [...seen];
}
